'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useRef } from 'react';
import { QuerySummarySkeleton } from '../Skeleton/QuerySummarySkeleton';
import { useSearchControllers } from './headless/SearchDefinitionContext';

const TITLE_KEY = 'search.page-title-prefix';
const RESULTS_LABEL_KEY = 'search.results-count-label';
const RESULTS_OF_KEY = 'search.results-count-of';
const TITLE_PLACEHOLDER = '{{search}}';
const DEFAULT_TITLE_TEMPLATE = `Search results for "${TITLE_PLACEHOLDER}"`;

export type QuerySummaryProps = {
  variant?: 'title' | 'count' | 'full';
  className?: string;
  testId?: string;
};

export const QuerySummary = ({
  variant = 'full',
  className,
  testId = 'query-summary-count',
}: QuerySummaryProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  const controllers = useSearchControllers();
  const { state } = controllers.useQuerySummary();
  // The seeded search parameters are part of the engine state, so unlike a
  // window.location probe they agree between the server render and hydration.
  const { state: searchParameterState } =
    controllers.useSearchParameterManager();
  const hasSearched = useRef(false);

  if (state.firstSearchExecuted) {
    hasSearched.current = true;
  }

  if (!state.hasResults && hasSearched.current) {
    return null;
  }

  if (!state.firstSearchExecuted) {
    // The title only ever renders for an active query — skip its skeleton
    // when there is none to avoid a layout shift when it disappears.
    if (variant === 'title' && !searchParameterState.parameters.q) return null;
    return <QuerySummarySkeleton variant={variant} className={className} />;
  }

  if (!state.hasResults) {
    return null;
  }

  const searchQuery = state.query;
  const resultsLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    RESULTS_LABEL_KEY,
    'Results'
  );
  const resultsOf = getMicrocopyValue(
    searchLabelsMicrocopy,
    RESULTS_OF_KEY,
    'of'
  );

  if (variant === 'count') {
    return (
      <p className={className} data-test={testId}>
        {resultsLabel}{' '}
        <strong>
          {state.firstResult} – {state.lastResult}
        </strong>{' '}
        {resultsOf} <strong>{state.total}</strong>
      </p>
    );
  }

  const buildTitleElement = () => {
    if (!searchQuery) return null;
    const raw = getMicrocopyValue(
      searchLabelsMicrocopy,
      TITLE_KEY,
      DEFAULT_TITLE_TEMPLATE
    );
    const template = raw.includes(TITLE_PLACEHOLDER)
      ? raw
      : DEFAULT_TITLE_TEMPLATE;
    const placeholderIndex = template.indexOf(TITLE_PLACEHOLDER);
    const prefix = template.slice(0, placeholderIndex);
    const suffix = template.slice(placeholderIndex + TITLE_PLACEHOLDER.length);
    return (
      <>
        <span className="font-normal">{prefix}</span>
        <strong>{searchQuery}</strong>
        <span className="font-normal">{suffix}</span>
      </>
    );
  };

  const titleElement = buildTitleElement();

  if (variant === 'title') {
    if (!titleElement) return null;
    return (
      <h1 className={className} data-test="search-page-title">
        {titleElement}
      </h1>
    );
  }

  return (
    <div className={className} data-test="query-summary">
      {titleElement && (
        <h1 className="header-large mb-4 lg:mb-6" data-test="search-page-title">
          {titleElement}
        </h1>
      )}
      <p className="body-small text-grey-600" data-test={testId}>
        {resultsLabel}{' '}
        <strong>
          {state.firstResult} – {state.lastResult}
        </strong>{' '}
        {resultsOf} <strong>{state.total}</strong>
      </p>
    </div>
  );
};
